import React, { useLayoutEffect } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NoticeBannersStackProvider, useResponsive } from '@sg/garnish';
import type { Location } from '@sg/graphql-schema';

import { LoadingAnimation, NotFoundView } from '@order/components';
import { useCustomScreenTitle } from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useReorderIfRequired } from '../../../ReorderingScreen';
import { useProductDetailsDeliveryRestaurantQuery } from '../../GraphQL/ProductDetailsDeliveryRestaurant.generated';
import {
  ProductDetailsScreenProvider,
  useProductDetailsScreenContext,
} from '../../ProductDetailsScreen.provider';
import { ProductDetailsContentV2 } from '../ProductDetailsContentV2/ProductDetailsContentV2';
import { ProductModals } from '../ProductModals';
import { ProductDetailsMD } from './ProductDetails.md';
import { ProductDetailsXS } from './ProductDetails.xs';

// ────────────────────────────────────────────────────────────────────────────────

export const ProductDetails = (props: ProductDetailsProps) => {
  const { testID } = props;

  return (
    <ProductDetailsScreenProvider {...props}>
      <NoticeBannersStackProvider>
        <ProductDetailsContent testID={testID} />
      </NoticeBannersStackProvider>
    </ProductDetailsScreenProvider>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

export const DeliveryProductDetails = (props: ProductDetailsProps) => {
  const { addressId, ingredientIds, name: customName } = props;

  // ─── Queries ─────────────────────────────────────────────────────

  const [{ fetching, data }] = useProductDetailsDeliveryRestaurantQuery({
    variables: { input: { addressId } },
    pause: !addressId,
  });

  // ─── Data ────────────────────────────────────────────────────────

  const locationByAddress = data?.locationByAddress as Location;
  const restaurant = locationByAddress?.restaurant;
  const restaurantSlug = restaurant?.slug;
  const vendor = locationByAddress?.vendor;
  const deliveryFee = restaurant?.deliveryFee ?? 0;

  // ─── Render Control ──────────────────────────────────────────────

  if (fetching) return <LoadingAnimation />;

  if (data !== undefined && !restaurant) return <NotFoundView />;

  return (
    <ProductDetails
      {...props}
      restaurantSlug={restaurantSlug}
      vendor={vendor}
      ingredientIds={ingredientIds}
      deliveryFee={deliveryFee}
      name={customName}
      isDelivery
    />
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const ProductDetailsContent = ({
  testID = 'pdp',
}: ProductDetailsContentProps) => {
  const { minWidth } = useResponsive();
  const { fetching, product, error, addressId, restaurantSlug } =
    useProductDetailsScreenContext();
  const productName = product?.name ?? '';

  useReorderIfRequired(restaurantSlug, addressId);

  // ─── Flags ───────────────────────────────────────────────────────────

  const shouldUseNewCustomization = useFeatureFlag(
    'CELS-2684-redesign-customization-enabled',
  );

  // ─── Navigation Options ──────────────────────────────────────────────

  useCustomScreenTitle(productName);
  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({ title: productName });
  }, [navigation, productName]);

  // ─────────────────────────────────────────────────────────────────────

  if (fetching) {
    return <LoadingAnimation />;
  }

  if (Boolean(error) || !product) {
    return <NotFoundView />;
  }

  if (shouldUseNewCustomization) {
    return (
      <>
        <ProductDetailsContentV2 />
        <ProductModals />
      </>
    );
  }

  return (
    <View style={styles.container} testID={testID?.concat('.container')}>
      {minWidth.isMD ? <ProductDetailsMD /> : <ProductDetailsXS />}

      <ProductModals />
    </View>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type ProductDetailsContentProps = Pick<ViewProps, 'testID'>;

type ProductDetailsProps = React.ComponentProps<
  typeof ProductDetailsScreenProvider
> &
  Pick<ViewProps, 'testID'> & { isDelivery?: boolean };
